import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, catchError, map, merge, of, switchMap } from 'rxjs';
import {
  SalesInsightsActivitiesResponse,
  SalesInsightsActivityRow,
  SalesInsightsCustomerHealthResponse,
  SalesInsightsCustomerHealthRow,
  SalesInsightsFilterValues
} from '../../models/sales-insights.model';
import { SalesInsightsActivityService } from '../../services/sales-insights-activity.service';
import {
  SalesInsightsCalendarView,
  salesInsightsActivityDay,
  salesInsightsCalendarDays,
  salesInsightsDayKey,
  salesInsightsDaysSinceVisit,
  salesInsightsIsoWeek,
  salesInsightsMovedActivityDates,
  salesInsightsParseDate,
  salesInsightsShiftCursor,
  salesInsightsVisitSuggestions
} from '../../utils/sales-insights-activities';
import { salesInsightsVisitTypeCode } from '../../utils/sales-insights-activity-types';
import { formatSalesInsightsAmount } from '../../utils/sales-insights-format';
import { salesInsightsLocale } from '../../utils/sales-insights-labels';
import { salesInsightsColor } from '../../utils/sales-insights-theme';
import { SalesInsightsBaseComponent } from '../sales-insights-base/sales-insights-base.component';

export interface SalesInsightsCalendarItem {
  row: SalesInsightsActivityRow;
  time: string;
  sortTime: number;
  typeName: string;
  typeRgb: string;
  typeIcon: string;
  overdue: boolean;
  title: string;
}

export interface SalesInsightsCalendarDay {
  key: string;
  number: number;
  weekday: string;
  label: string;
  outside: boolean;
  today: boolean;
  past: boolean;
  weekend: boolean;
  items: SalesInsightsCalendarItem[];
}

export interface SalesInsightsCalendarWeek {
  number: number;
  days: SalesInsightsCalendarDay[];
}

export interface SalesInsightsCalendarLegendEntry {
  id: string;
  name: string;
  rgb: string;
  icon: string;
}

export interface SalesInsightsVisitSuggestion {
  row: SalesInsightsCustomerHealthRow;
  sinceKey: string;
  sinceParams: { [name: string]: string };
  booked: string | null;
}

const DEFAULT_MAX_PER_DAY = 3;
const DEFAULT_VISIT_SUGGESTIONS = 6;
const NOT_VISITED_DAYS = 90;

@Component({
  selector: 'lib-sales-insights-activity-calendar',
  standalone: false,
  templateUrl: './sales-insights-activity-calendar.component.html',
  styleUrl: './sales-insights-activity-calendar.component.scss'
})
export class SalesInsightsActivityCalendarComponent
  extends SalesInsightsBaseComponent<SalesInsightsActivitiesResponse>
  implements OnInit
{
  protected translate = inject(TranslateService);
  protected activityService = inject(SalesInsightsActivityService);
  private destroy = inject(DestroyRef);

  view: SalesInsightsCalendarView = 'month';
  cursor = new Date();
  days: Date[] = [];
  weeks: SalesInsightsCalendarWeek[] = [];
  weekdays: string[] = [];
  legend: SalesInsightsCalendarLegendEntry[] = [];
  visitType: string | null = null;
  heading = '';
  bookDate: Date = new Date();

  selectedKey = salesInsightsDayKey(new Date());
  selectedDay: SalesInsightsCalendarDay | null = null;
  visits: SalesInsightsVisitSuggestion[] = [];
  visitTotal = '';
  visitsLoading = false;

  dragId: string | null = null;
  dropDay: string | null = null;
  saving = new Set<string>();
  moveError: string | null = null;
  booking = false;

  private rows: SalesInsightsActivityRow[] = [];
  private rangeChanges = new Subject<void>();

  ngOnInit(): void {
    this.view = this.options.view === 'week' ? 'week' : 'month';
    this.days = salesInsightsCalendarDays(this.view, this.cursor);
    this.heading = this.headingText();
    super.ngOnInit();
    if (this.sidePanel && this.visitLimit > 0) {
      this.loadVisits();
    }
  }

  get canBook(): boolean {
    return !!this.options.bookActivity;
  }

  get canDrag(): boolean {
    return !!this.options.drag;
  }

  get sidePanel(): boolean {
    return this.options.sidePanel !== false;
  }

  get visitLimit(): number {
    const limit = this.options.visitSuggestions;
    return limit === undefined || limit === null ? DEFAULT_VISIT_SUGGESTIONS : Math.max(0, Number(limit) || 0);
  }

  get maxPerDay(): number {
    return this.view === 'week' ? Number.MAX_SAFE_INTEGER : this.options.maxPerDay || DEFAULT_MAX_PER_DAY;
  }

  get selectedIsToday(): boolean {
    return this.selectedKey === salesInsightsDayKey(new Date());
  }

  protected fetch(filters: SalesInsightsFilterValues): Observable<SalesInsightsActivitiesResponse> {
    return this.activityService.loadTypes().pipe(
      switchMap(() =>
        this.salesInsightsService.getData<SalesInsightsActivitiesResponse>(
          {
            endpoint: this.config.source?.endpoint || 'activities',
            params: {
              ...(this.config.source?.params || {}),
              from: salesInsightsDayKey(this.days[0]),
              to: salesInsightsDayKey(this.days[this.days.length - 1])
            }
          },
          filters,
          this.config.accepts
        )
      )
    );
  }

  protected reloads(): Observable<unknown> {
    return merge(this.dashboardState.changes('activities', 'activityTypes'), this.rangeChanges);
  }

  // The grid is shown also for a range without activities, so the user can move on to the next one.
  protected isEmpty(_data: SalesInsightsActivitiesResponse | null): boolean {
    return false;
  }

  protected onData(data: SalesInsightsActivitiesResponse | null): void {
    this.rows = data?.rows || [];
    this.visitType = salesInsightsVisitTypeCode(this.activityService.activityTypes);
    this.legend = this.activityService.typeOptions().map(type => ({
      id: type.id,
      name: type.name,
      rgb: salesInsightsColor(this.activityService.typeColorIndex(type.id, this.options.typeColors)),
      icon: this.activityService.typeIcon(type.id, this.options.typeIcons)
    }));
    this.build();
  }

  setView(view: SalesInsightsCalendarView): void {
    if (this.view !== view) {
      this.view = view;
      this.changeRange(salesInsightsParseDate(this.selectedKey) || this.cursor);
    }
  }

  step(step: number): void {
    this.changeRange(salesInsightsShiftCursor(this.view, this.cursor, step));
  }

  goToday(): void {
    this.selectedKey = salesInsightsDayKey(new Date());
    this.changeRange(new Date());
  }

  selectDay(day: SalesInsightsCalendarDay): void {
    this.selectedKey = day.key;
    this.selectedDay = day;
  }

  showWeek(day: SalesInsightsCalendarDay, event: Event): void {
    event.stopPropagation();
    this.view = 'week';
    this.selectedKey = day.key;
    this.changeRange(salesInsightsParseDate(day.key) || this.cursor);
  }

  open(item: SalesInsightsCalendarItem, event?: Event): void {
    event?.stopPropagation();
    if (this.dragId) {
      return;
    }
    this.activityService
      .openActivity(item.row.id)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        complete: () => this.dashboardState.notifyChanged('activities'),
        error: () => (this.moveError = 'SALES_INSIGHTS_LOAD_FAILED')
      });
  }

  openCustomer(customerId: string): void {
    this.activityService.openCustomer(customerId);
  }

  // A plain activity on the day; the toolbar button is the one that also offers the goals.
  quickBook(day: SalesInsightsCalendarDay, event: Event): void {
    event.stopPropagation();
    const date = salesInsightsParseDate(day.key);
    if (!date || this.booking) {
      return;
    }
    this.booking = true;
    this.activityService
      .book(null, date, null)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: saved => {
          this.booking = false;
          if (saved) {
            this.dashboardState.notifyChanged('activities');
          }
        },
        error: () => (this.booking = false)
      });
  }

  dragStart(item: SalesInsightsCalendarItem, event: DragEvent): void {
    if (!this.canDrag || this.saving.has(item.row.id)) {
      event.preventDefault();
      return;
    }
    this.dragId = item.row.id;
    event.dataTransfer?.setData('text/plain', item.row.id);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
    }
  }

  dragOver(day: SalesInsightsCalendarDay, event: DragEvent): void {
    if (this.dragId) {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      this.dropDay = day.key;
    }
  }

  dragLeave(day: SalesInsightsCalendarDay): void {
    if (this.dropDay === day.key) {
      this.dropDay = null;
    }
  }

  dragEnd(): void {
    this.dragId = null;
    this.dropDay = null;
  }

  drop(day: SalesInsightsCalendarDay, event: DragEvent): void {
    event.preventDefault();
    const id = this.dragId || event.dataTransfer?.getData('text/plain') || null;
    this.dragEnd();
    if (id) {
      this.move(id, day.key);
    }
  }

  // Moves the event at once and puts it back if the save fails.
  move(id: string, targetDay: string): void {
    const row = this.rows.find(r => r.id === id);
    if (!row || salesInsightsActivityDay(row.activityDate) === targetDay) {
      return;
    }
    const moved = salesInsightsMovedActivityDates(row.activityDate, row.activityEndDate, targetDay);
    if (!moved) {
      return;
    }
    const previous = { activityDate: row.activityDate, activityEndDate: row.activityEndDate };
    row.activityDate = moved.activityDate;
    row.activityEndDate = moved.activityEndDate ?? row.activityEndDate;
    this.moveError = null;
    this.saving.add(id);
    this.build();
    this.activityService
      .move(id, targetDay)
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe({
        next: () => {
          this.saving.delete(id);
          this.build();
          this.dashboardState.notifyChanged('activities');
        },
        error: err => {
          this.saving.delete(id);
          row.activityDate = previous.activityDate;
          row.activityEndDate = previous.activityEndDate;
          this.moveError = err?.error?.message || 'SALES_INSIGHTS_ACTIVITY_MOVE_FAILED';
          this.build();
        }
      });
  }

  private changeRange(cursor: Date): void {
    this.cursor = cursor;
    this.days = salesInsightsCalendarDays(this.view, cursor);
    this.heading = this.headingText();
    if (!this.days.some(day => salesInsightsDayKey(day) === this.selectedKey)) {
      this.selectedKey = salesInsightsDayKey(this.firstBookableDay(salesInsightsDayKey(new Date())));
    }
    this.build();
    this.rangeChanges.next();
  }

  private build(): void {
    const locale = salesInsightsLocale(this.translate);
    const today = salesInsightsDayKey(new Date());
    this.bookDate = this.firstBookableDay(today);
    const byDay = new Map<string, SalesInsightsCalendarItem[]>();
    this.rows.forEach(row => {
      const key = salesInsightsActivityDay(row.activityDate);
      if (key) {
        byDay.set(key, [...(byDay.get(key) || []), this.toItem(row, key, today, locale)]);
      }
    });
    this.weekdays = this.days.slice(0, 7).map(day => day.toLocaleDateString(locale, { weekday: 'short' }));
    this.weeks = [];
    for (let i = 0; i < this.days.length; i += 7) {
      const week = this.days.slice(i, i + 7);
      this.weeks.push({
        number: salesInsightsIsoWeek(week[0]),
        days: week.map(date => {
          const key = salesInsightsDayKey(date);
          return {
            key,
            number: date.getDate(),
            weekday: date.toLocaleDateString(locale, { weekday: 'short' }),
            label: date.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' }),
            outside: this.view === 'month' && date.getMonth() !== this.cursor.getMonth(),
            today: key === today,
            past: key < today,
            weekend: date.getDay() === 0 || date.getDay() === 6,
            items: (byDay.get(key) || []).sort((a, b) => a.sortTime - b.sortTime)
          };
        })
      });
    }
    this.selectedDay = this.weeks.flatMap(week => week.days).find(day => day.key === this.selectedKey) || null;
  }

  private toItem(row: SalesInsightsActivityRow, day: string, today: string, locale: string): SalesInsightsCalendarItem {
    const date = salesInsightsParseDate(row.activityDate);
    const hasTime = !!date && (date.getHours() !== 0 || date.getMinutes() !== 0);
    const typeName = this.activityService.typeName(row.type);
    const time = hasTime ? date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '';
    return {
      row,
      time,
      sortTime: date?.getTime() ?? 0,
      typeName,
      typeRgb: salesInsightsColor(this.activityService.typeColorIndex(row.type, this.options.typeColors)),
      typeIcon: this.activityService.typeIcon(row.type, this.options.typeIcons),
      overdue: !row.done && day < today,
      title: [
        time,
        typeName,
        row.customerName,
        row.city,
        row.customerResponsibleName || row.customerResponsible,
        row.comment
      ]
        .filter(part => !!part)
        .join('\n')
    };
  }

  // Customers that need a visit and have not had one for 90 days, from the follow-up data the summary counts.
  private loadVisits(): void {
    merge(
      this.dashboardState.filters$,
      this.dashboardState.changes('activities', 'activityTypes').pipe(map(() => this.dashboardState.filters))
    )
      .pipe(
        switchMap(filters => {
          this.visitsLoading = !this.visits.length;
          return this.salesInsightsService
            .getData<SalesInsightsCustomerHealthResponse>(
              {
                endpoint: 'customer-health',
                params: { sort: 'lastContact' }
              },
              filters
            )
            .pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroy)
      )
      .subscribe(response => {
        const locale = salesInsightsLocale(this.translate);
        const candidates = salesInsightsVisitSuggestions(response?.rows || [], NOT_VISITED_DAYS);
        this.visitTotal = formatSalesInsightsAmount(candidates.length);
        this.visits = candidates.slice(0, this.visitLimit).map(row => this.toVisit(row, locale));
        this.visitsLoading = false;
      });
  }

  private toVisit(row: SalesInsightsCustomerHealthRow, locale: string): SalesInsightsVisitSuggestion {
    const next = salesInsightsParseDate(row.nextContact?.date);
    const days = salesInsightsDaysSinceVisit(row);
    return {
      row,
      sinceKey: days === null || days === undefined ? 'SALES_INSIGHTS_VISIT_NEVER' : 'SALES_INSIGHTS_VISIT_DAYS_SINCE',
      sinceParams: { count: formatSalesInsightsAmount(days ?? 0) },
      booked: next ? next.toLocaleDateString(locale, { day: 'numeric', month: 'short' }) : null
    };
  }

  // Today while it is on screen, otherwise the first day of the shown month or week.
  private firstBookableDay(today: string): Date {
    const shown = this.days.filter(day => this.view === 'week' || day.getMonth() === this.cursor.getMonth());
    const current = shown.find(day => salesInsightsDayKey(day) === today);
    return current ? new Date() : shown[0] || this.cursor;
  }

  private headingText(): string {
    const locale = salesInsightsLocale(this.translate);
    if (this.view === 'month') {
      return this.cursor.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    const first = this.days[0];
    const last = this.days[this.days.length - 1];
    const range = `${first.toLocaleDateString(locale, { day: 'numeric', month: 'short' })} → ${last.toLocaleDateString(
      locale,
      {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }
    )}`;
    return `${this.translate.instant('SALES_INSIGHTS_WEEK')} ${salesInsightsIsoWeek(first)} · ${range}`;
  }
}
